import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import { useUserPreferences } from "./useUserPreferences";
import { detectInitialLanguage, persistLanguageCookie } from "@/lib/initialLanguage";

export type Language = "cs" | "en";

interface Translations {
  // Dashboard
  financialOverview: string;
  overview: string;
  selectMonth: string;
  transactionDetails: string;
  transactionsWord: string;
  totalTransactions: string;
  transactionCount: string;
  matched: string;
  successfullyMatched: string;
  unmatched: string;
  awaitingMatching: string;
  all: string;

  // Months
  months: string[];

  // Buttons
  fetchTransactions: string;
  syncReceipts: string;
  aiMatch: string;
  uploadInvoice: string;

  // Tables
  date: string;
  description: string;
  amount: string;
  category: string;
  status: string;
  invoice: string;
  notes: string;
  owner: string;
  ownerAll: string;
  ownerPetr: string;
  ownerOndra: string;
  ownerShared: string;
  ownerConversion: string;
  sharedSplit: string;
  petrPercent: string;
  addNote: string;
  editNote: string;
  notePlaceholder: string;
  noteSaved: string;
  vendor: string;
  file: string;

  // Match section
  monthlySummary: string;
  matchRate: string;
  transactionsMatched: string;
  income: string;
  expenses: string;
  uploadedInvoices: string;
  noInvoices: string;
  deleteInvoice: string;
  invoiceDeleted: string;
  deleteError: string;
  filterAll: string;
  filterMatched: string;
  filterUnmatched: string;
  receiptShow: string;
  receiptEdit: string;
  receiptUnmatch: string;
  receiptDelete: string;
  manualMatching: string;
  dragInvoice: string;
  dragHere: string;

  // Categories
  noCategory: string;

  // Messages
  noTransactions: string;
  loadingAccounts: string;
  waitingFioLimit: string;
  preparing: string;
  loading: string;
  transactionsLoaded: string;
  period: string;
  newOf: string;
  total: string;
  matchingComplete: string;
  matchesCreated: string;
  ofSuggestions: string;
  paired: string;
  unpaired: string;
  pairingCancelled: string;
  transactionUnpaired: string;
  error: string;
  pairingError: string;
  unpairingError: string;
  noBankAccounts: string;
  addBankAccounts: string;
  // Status options
  statusMatched: string;
  statusUnmatched: string;
  statusMissing: string;
  statusNotNeeded: string;
  statusIncomingInvoice: string;
  // Stat band
  statTransactions: string;
  statIncome: string;
  statExpense: string;
  statOf: string;
  statUnmatchedSuffix: string;
  statNet: string;
  statDone: string;
  /** "{n}" is replaced with the number of rows still to resolve. */
  statRemaining: string;
  statNoTransactions: string;
  statCurrent: string;
  stepPrevMonth: string;
  stepNextMonth: string;
  stepPrevYear: string;
  stepNextYear: string;
  statusBreakdown: string;
  linkInvoice: string;
  searchInvoices: string;
  noInvoicesFound: string;
  // Filters
  filters: string;
  searchPlaceholder: string;
  columns: string;
  moreActions: string;
  accountingCheck: string;
  uploadReceipts: string;
  clearFilters: string;
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  missing: string;
  notNeeded: string;
  // Categories
  categories: Record<string, string>;
  // Profile & Team
  personalInfo: string;
  personalInfoDesc: string;
  emailCantChange: string;
  fullName: string;
  saveChanges: string;
  logout: string;
  logoutDesc: string;
  logoutButton: string;
  // Organization switcher
  switchOrganization: string;
  myCompany: string;
  noOrganizationSelected: string;
  noOrganizations: string;
  roleOwner: string;
  roleAdmin: string;
  roleMember: string;
  // Settings page
  settings: string;
  settingsDesc: string;
  bankAccounts: string;
  bankAccountsDesc: string;
  apiAccounts: string;
  apiAccountsDesc: string;
  // Bank accounts
  addBankAccount: string;
  accountName: string;
  currency: string;
  fioApiToken: string;
  fioTokenHelp: string;
  accountNamePlaceholder: string;
  cancel: string;
  addAccount: string;
  accountAdded: string;
  accountAddedDesc: string;
  accountDeleted: string;
  accountDeletedDesc: string;
  lastSync: string;
  never: string;
  currentMonth: string;
  last3Months: string;
  last6Months: string;
  wholeYear: string;
  syncComplete: string;
  loadedTransactions: string;
  fromAccount: string;
  forMonths: string;
  syncError: string;
  unknownError: string;
  setupInstructions: string;
  getApiToken: string;
  downloadOlderData: string;
  multipleAccounts: string;
  multipleAccountsDesc: string;
  // Bank accounts for invoices (Profile + Invoice form)
  bankAccountsForInvoices: string;
  bankAccountsForInvoicesDesc: string;
  bankAccount: string;
  noBankAccount: string;
  accountNumber: string;
  bankCode: string;
  iban: string;
  swift: string;
  bankName: string;
  addPaymentAccount: string;
  editBankAccount: string;
  // Shared status labels
  active: string;
  addFolderError: string;
  skipped: string;
  // Analytics
  analytics: string;
  balance: string;
  exchangeRate: string;
  // Pages
  dashboard: string;
  invoices: string;
  clients: string;
  templates: string;
  profile: string;
  general: string;
  // Feedback
  tagImprovement: string;
  tagIdea: string;
  tagBug: string;
  tagProfile: string;
  tagOther: string;
  feedback: string;
  feedbackDesc: string;
  addYourFirstNote: string;
  noFeedbackYet: string;
  noFeedbackDesc: string;
  editFeedbackNote: string;
  addFeedbackNote: string;
  page: string;
  title: string;
  content: string;
  tags: string;
  postAnonymously: string;
  yourName: string;
  usingNameFromProfile: string;
  showMore: string;
  showLess: string;
  anonymous: string;
  success: string;
  feedbackNoteCreated: string;
  feedbackNoteUpdated: string;
  feedbackNoteDeleted: string;
  failedToCreateNote: string;
  failedToUpdateNote: string;
  failedToDeleteNote: string;
  saving: string;
  create: string;
  update: string;
  deleteConfirm: string;
  // Import/Export
  transactions: string;
  receiptsOverview: string;
  importExport: string;
  importTransactions: string;
  exportTransactions: string;
  selectCsvFile: string;
  preview: string;
  import: string;
  export: string;
  transactionsImportedSuccessfully: string;
  duplicatesSkipped: string;
  syncErrorBannerTitle: string;
  syncErrorBannerDescription: string;
  syncErrorBannerAction: string;
  settingsPage: string;
  connecting: string;
}

const translations: Record<Language, Translations> = {
  cs: {
    financialOverview: "Finanční přehled",
    overview: "Přehled",
    selectMonth: "Vyberte měsíc pro zobrazení transakcí",
    transactionDetails: "Detail transakcí za vybraný měsíc",
    transactionsWord: "transakcí",
    totalTransactions: "Celkem transakcí",
    transactionCount: "Počet všech transakcí",
    matched: "Spárováno",
    successfullyMatched: "Úspěšně spárované",
    unmatched: "Nespárováno",
    awaitingMatching: "Čekající na párování",
    all: "Vše",
    months: [
      "Leden",
      "Únor",
      "Březen",
      "Duben",
      "Květen",
      "Červen",
      "Červenec",
      "Srpen",
      "Září",
      "Říjen",
      "Listopad",
      "Prosinec",
    ],
    fetchTransactions: "Načíst transakce",
    syncReceipts: "Sync účtenek",
    aiMatch: "AI párování",
    uploadInvoice: "Nahrát fakturu",
    date: "Datum",
    description: "Popis",
    amount: "Částka",
    category: "Kategorie",
    status: "Stav",
    invoice: "Faktura",
    notes: "Poznámky",
    owner: "Vlastník",
    ownerAll: "Všichni",
    ownerPetr: "Petr",
    ownerOndra: "Ondra",
    ownerShared: "Společné",
    ownerConversion: "Konverze",
    sharedSplit: "Společné rozdělení",
    petrPercent: "Petr %",
    addNote: "Přidat poznámku",
    editNote: "Upravit poznámku",
    notePlaceholder: "Napište poznámku…",
    noteSaved: "Poznámka uložena",
    vendor: "Dodavatel",
    file: "Soubor",
    monthlySummary: "Měsíční přehled",
    matchRate: "Míra spárování",
    transactionsMatched: "transakcí spárováno",
    income: "Příjmy",
    expenses: "Výdaje",
    uploadedInvoices: "Nahrané faktury",
    noInvoices: "Žádné faktury",
    deleteInvoice: "Smazat fakturu",
    invoiceDeleted: "Faktura smazána",
    deleteError: "Nepodařilo se smazat fakturu",
    filterAll: "Vše",
    filterMatched: "Spárované",
    filterUnmatched: "Nespárované",
    receiptShow: "Zobrazit doklad",
    receiptEdit: "Upravit",
    receiptUnmatch: "Zrušit párování",
    receiptDelete: "Smazat",
    manualMatching: "Ruční párování",
    dragInvoice: "Přetáhněte fakturu na řádek transakce pro ruční spárování",
    dragHere: "Přetáhněte sem",
    noCategory: "Bez kategorie",
    noTransactions: "Pro toto období nebyly nalezeny žádné transakce",
    loadingAccounts: "Načítám",
    waitingFioLimit: "Čekám 30s (Fio limit)",
    preparing: "Připravuji",
    loading: "Načítám",
    transactionsLoaded: "Transakce načteny",
    period: "Období",
    newOf: "nových z",
    total: "celkem",
    matchingComplete: "AI Párování dokončeno",
    matchesCreated: "Vytvořeno",
    ofSuggestions: "nových párů z",
    paired: "Spárováno",
    unpaired: "Transakce a faktura byly úspěšně spárovány",
    pairingCancelled: "Párování zrušeno",
    transactionUnpaired: "Transakce byla odpárována od faktury",
    error: "Chyba",
    pairingError: "Nepodařilo se spárovat transakci s fakturou",
    unpairingError: "Nepodařilo se zrušit párování",
    noBankAccounts: "Žádné bankovní účty",
    addBankAccounts: "Přidejte bankovní účty v Nastavení",
    statusMatched: "Spárováno",
    statusUnmatched: "Nespárováno",
    statusMissing: "Chybí",
    statusNotNeeded: "Nepotřebné",
    statusIncomingInvoice: "Faktura",
    statTransactions: "Transakce",
    statIncome: "Příjmy",
    statExpense: "Výdaje",
    statOf: "z",
    statUnmatchedSuffix: "nespárováno",
    statNet: "Saldo",
    statDone: "Hotovo",
    statRemaining: "zbývá {n}",
    statNoTransactions: "Bez transakcí",
    statCurrent: "Aktuální",
    stepPrevMonth: "Předchozí měsíc",
    stepNextMonth: "Další měsíc",
    stepPrevYear: "Předchozí rok",
    stepNextYear: "Další rok",
    statusBreakdown: "Stav párování",
    linkInvoice: "Připojit fakturu",
    searchInvoices: "Hledat faktury...",
    noInvoicesFound: "Žádné faktury nenalezeny",
    filters: "Filtry",
    searchPlaceholder: "Hledat…",
    columns: "Sloupce",
    moreActions: "Více akcí",
    accountingCheck: "Účetní kontrola",
    uploadReceipts: "Nahrát účtenky",
    clearFilters: "Zrušit filtry",
    dateFrom: "Od",
    dateTo: "Do",
    amountMin: "Min částka",
    amountMax: "Max částka",
    missing: "Chybí",
    notNeeded: "Nepotřebné",
    categories: {
      "Card Payment": "Platba kartou",
      Software: "Software",
      Fees: "Poplatky",
      Shopping: "Nákupy",
      Travel: "Cestování",
      Business: "Podnikání",
      Food: "Stravování",
      Hosting: "Hosting",
      Tax: "Daně",
      Other: "Ostatní",
      Salary: "Mzda",
      Income: "Příjem",
      Refund: "Vratka",
    },
    // Profile & Team
    personalInfo: "Osobní údaje",
    personalInfoDesc: "Aktualizujte své jméno a firemní údaje",
    emailCantChange: "Email nelze změnit",
    fullName: "Jméno a příjmení",
    saveChanges: "Uložit změny",
    logout: "Odhlášení",
    logoutDesc: "Odhlaste se ze svého účtu",
    logoutButton: "Odhlásit se",
    // Organization switcher
    switchOrganization: "Přepnout organizaci",
    myCompany: "Moje organizace",
    noOrganizationSelected: "Nevybraná organizace",
    noOrganizations: "Žádné organizace",
    roleOwner: "Vlastník",
    roleAdmin: "Správce",
    roleMember: "Člen",
    // Settings
    settings: "Nastavení",
    settingsDesc: "Konfigurace webhooků a zobrazení API endpointů.",
    bankAccounts: "Bankovní účty",
    bankAccountsDesc:
      "Přidejte API tokeny pro automatické stahování transakcí z vašich bankovních účtů.",
    apiAccounts: "API účty (propojené banky)",
    apiAccountsDesc:
      "Účty připojené přes API pro synchronizaci transakcí. Platební údaje (IBAN) pro faktury přidáte v Profilu.",
    // Bank accounts
    addBankAccount: "Přidat bankovní účet",
    accountName: "Název účtu",
    currency: "Měna",
    fioApiToken: "Fio API Token",
    fioTokenHelp: "Token najdete v Internet Bankingu Fio: Nastavení → API.",
    accountNamePlaceholder: "např. Firemní účet EUR",
    cancel: "Zrušit",
    addAccount: "Přidat účet",
    accountAdded: "Účet přidán",
    accountAddedDesc: "Bankovní účet byl úspěšně přidán.",
    accountDeleted: "Účet smazán",
    accountDeletedDesc: "Bankovní účet byl úspěšně smazán.",
    lastSync: "Poslední sync",
    never: "Nikdy",
    currentMonth: "Aktuální měsíc",
    last3Months: "Poslední 3 měsíce",
    last6Months: "Poslední 6 měsíců",
    wholeYear: "Celý rok",
    syncComplete: "Synchronizace dokončena",
    loadedTransactions: "Načteno",
    fromAccount: "transakcí z účtu",
    forMonths: "za měsíců",
    syncError: "Chyba synchronizace",
    unknownError: "Neznámá chyba",
    setupInstructions: "Návod na nastavení",
    getApiToken: "Získání API tokenu",
    downloadOlderData: "Stažení starších dat (90+ dní)",
    multipleAccounts: "Více účtů",
    multipleAccountsDesc:
      "Můžete přidat více účtů (např. CZK a EUR účet). Každý účet potřebuje vlastní API token.",
    bankAccountsForInvoices: "Účty pro platby na fakturách (IBAN)",
    bankAccountsForInvoicesDesc:
      "Platební údaje (IBAN) zobrazené na fakturách. Pro synchronizaci transakcí použijte API účty v Nastavení.",
    bankAccount: "Účet pro platbu",
    noBankAccount: "— Žádný —",
    accountNumber: "Číslo účtu",
    bankCode: "Kód banky",
    bankName: "Název banky",
    iban: "IBAN",
    swift: "SWIFT/BIC",
    addPaymentAccount: "Přidat účet",
    editBankAccount: "Upravit",
    // Shared status labels
    active: "Aktivní",
    addFolderError: "Nepodařilo se přidat složku",
    skipped: "Přeskočeno",
    // Analytics
    analytics: "Analytika",
    balance: "Bilance",
    exchangeRate: "Kurz EUR/CZK",
    // Pages
    dashboard: "Přehled",
    invoices: "Faktury",
    clients: "Klienti",
    templates: "Šablony",
    settingsPage: "Nastavení",
    connecting: "Připojuji…",
    profile: "Profil",
    general: "Obecné",
    // Feedback
    tagImprovement: "Zlepšení",
    tagIdea: "Nápad",
    tagBug: "Chyba/Přestalo fungovat",
    tagProfile: "Profil",
    tagOther: "Ostatní",
    feedback: "Zpětná vazba",
    feedbackDesc: "Sdílejte své nápady, nahlaste problémy a pomozte zlepšit aplikaci",
    addYourFirstNote: "Přidejte svou první poznámku",
    noFeedbackYet: "Zatím žádná zpětná vazba",
    noFeedbackDesc: 'Buďte první, kdo sdílí své myšlenky! Klikněte na "Přidat poznámku" a začněte.',
    editFeedbackNote: "Upravit poznámku",
    addFeedbackNote: "Přidat poznámku",
    page: "Stránka",
    title: "Nadpis",
    content: "Obsah",
    tags: "Štítky",
    postAnonymously: "Zveřejnit anonymně",
    yourName: "Vaše jméno",
    usingNameFromProfile: "Používá se jméno z vašeho profilu. Můžete ho zde změnit.",
    showMore: "Zobrazit více",
    showLess: "Zobrazit méně",
    anonymous: "Anonymní",
    success: "Úspěch",
    feedbackNoteCreated: "Poznámka byla úspěšně vytvořena",
    feedbackNoteUpdated: "Poznámka byla úspěšně aktualizována",
    feedbackNoteDeleted: "Poznámka byla úspěšně smazána",
    failedToCreateNote: "Nepodařilo se vytvořit poznámku",
    failedToUpdateNote: "Nepodařilo se aktualizovat poznámku",
    failedToDeleteNote: "Nepodařilo se smazat poznámku",
    saving: "Ukládání...",
    create: "Vytvořit",
    update: "Aktualizovat",
    deleteConfirm: "Opravdu chcete smazat tuto poznámku?",
    // Import/Export
    transactions: "Transakce",
    receiptsOverview: "Přehled účtenek",
    importExport: "Import / Export",
    importTransactions: "Importovat transakce",
    exportTransactions: "Exportovat transakce",
    selectCsvFile: "Vybrat CSV soubor",
    preview: "Náhled",
    import: "Importovat",
    export: "Exportovat",
    transactionsImportedSuccessfully: "Transakce úspěšně importovány",
    duplicatesSkipped: "duplikáty přeskočeny",
    syncErrorBannerTitle: "Propojení bankovního účtu selhalo",
    syncErrorBannerDescription:
      "Nepodařilo se načíst transakce z účtu. Prosím znovu uložte API token v nastavení:",
    syncErrorBannerAction: "Přejít do nastavení",
  },
  en: {
    financialOverview: "Financial overview",
    overview: "Overview",
    selectMonth: "Select a month to view transactions",
    transactionDetails: "Transaction details for selected month",
    transactionsWord: "transactions",
    totalTransactions: "Total transactions",
    transactionCount: "Number of all transactions",
    matched: "Matched",
    successfullyMatched: "Successfully matched",
    unmatched: "Unmatched",
    awaitingMatching: "Awaiting matching",
    all: "All",
    months: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    fetchTransactions: "Fetch transactions",
    syncReceipts: "Sync receipts",
    aiMatch: "AI match",
    uploadInvoice: "Upload invoice",
    date: "Date",
    description: "Description",
    amount: "Amount",
    category: "Category",
    status: "Status",
    invoice: "Invoice",
    notes: "Notes",
    owner: "Owner",
    ownerAll: "All owners",
    ownerPetr: "Petr",
    ownerOndra: "Ondra",
    ownerShared: "Shared",
    ownerConversion: "Conversion",
    sharedSplit: "Shared split",
    petrPercent: "Petr %",
    addNote: "Add note",
    editNote: "Edit note",
    notePlaceholder: "Write a note…",
    noteSaved: "Note saved",
    vendor: "Vendor",
    file: "File",
    monthlySummary: "Monthly summary",
    matchRate: "Match rate",
    transactionsMatched: "transactions matched",
    income: "Income",
    expenses: "Expenses",
    uploadedInvoices: "Uploaded invoices",
    noInvoices: "No invoices",
    deleteInvoice: "Delete invoice",
    invoiceDeleted: "Invoice deleted",
    deleteError: "Failed to delete invoice",
    filterAll: "All",
    filterMatched: "Matched",
    filterUnmatched: "Unmatched",
    receiptShow: "View document",
    receiptEdit: "Edit",
    receiptUnmatch: "Unmatch",
    receiptDelete: "Delete",
    manualMatching: "Manual matching",
    dragInvoice: "Drag an invoice onto a transaction row to match manually",
    dragHere: "Drop here",
    noCategory: "No category",
    noTransactions: "No transactions found for this period",
    loadingAccounts: "Loading",
    waitingFioLimit: "Waiting 30s (Fio limit)",
    preparing: "Preparing",
    loading: "Loading",
    transactionsLoaded: "Transactions loaded",
    period: "Period",
    newOf: "new of",
    total: "total",
    matchingComplete: "AI Matching complete",
    matchesCreated: "Created",
    ofSuggestions: "new matches from",
    paired: "Matched",
    unpaired: "Transaction and invoice successfully matched",
    pairingCancelled: "Match cancelled",
    transactionUnpaired: "Transaction unmatched from invoice",
    error: "Error",
    pairingError: "Failed to match transaction with invoice",
    unpairingError: "Failed to cancel matching",
    noBankAccounts: "No bank accounts",
    addBankAccounts: "Add bank accounts in Settings",
    statusMatched: "Matched",
    statusUnmatched: "Unmatched",
    statusMissing: "Missing",
    statusNotNeeded: "Not needed",
    statusIncomingInvoice: "Invoice",
    statTransactions: "Transactions",
    statIncome: "Income",
    statExpense: "Expenses",
    statOf: "of",
    statUnmatchedSuffix: "unmatched",
    statNet: "Net",
    statDone: "Done",
    statRemaining: "{n} left",
    statNoTransactions: "No transactions",
    statCurrent: "Current",
    stepPrevMonth: "Previous month",
    stepNextMonth: "Next month",
    stepPrevYear: "Previous year",
    stepNextYear: "Next year",
    statusBreakdown: "Matching status",
    linkInvoice: "Link invoice",
    searchInvoices: "Search invoices...",
    noInvoicesFound: "No invoices found",
    filters: "Filters",
    searchPlaceholder: "Search…",
    columns: "Columns",
    moreActions: "More actions",
    accountingCheck: "Accounting check",
    uploadReceipts: "Upload receipts",
    clearFilters: "Clear filters",
    dateFrom: "From",
    dateTo: "To",
    amountMin: "Min amount",
    amountMax: "Max amount",
    missing: "Missing",
    notNeeded: "Not needed",
    categories: {
      "Card Payment": "Card",
      Software: "Software",
      Fees: "Fees",
      Shopping: "Shopping",
      Travel: "Travel",
      Business: "Business",
      Food: "Food",
      Hosting: "Hosting",
      Tax: "Tax",
      Other: "Other",
      Salary: "Salary",
      Income: "Income",
      Refund: "Refund",
    },
    // Profile & Team
    personalInfo: "Personal info",
    personalInfoDesc: "Update your name and company details",
    emailCantChange: "Email cannot be changed",
    fullName: "Full name",
    saveChanges: "Save changes",
    logout: "Sign out",
    logoutDesc: "Sign out of your account",
    logoutButton: "Sign out",
    // Organization switcher
    switchOrganization: "Switch organization",
    myCompany: "My organization",
    noOrganizationSelected: "No organization selected",
    noOrganizations: "No organizations",
    roleOwner: "Owner",
    roleAdmin: "Admin",
    roleMember: "Member",
    // Settings
    settings: "Settings",
    settingsDesc: "Configure webhooks and view API endpoints.",
    bankAccounts: "Bank accounts",
    bankAccountsDesc: "Add API tokens for automatic transaction fetching from your bank accounts.",
    apiAccounts: "API Accounts (connected banks)",
    apiAccountsDesc:
      "Accounts connected via API for transaction sync. For payment details (IBAN) on invoices, add them in Profile.",
    // Bank accounts
    addBankAccount: "Add bank account",
    accountName: "Account name",
    currency: "Currency",
    fioApiToken: "Fio API Token",
    fioTokenHelp: "Find your token in Fio Internet Banking: Settings → API.",
    accountNamePlaceholder: "e.g. Business Account EUR",
    cancel: "Cancel",
    addAccount: "Add account",
    accountAdded: "Account added",
    accountAddedDesc: "Bank account was successfully added.",
    accountDeleted: "Account deleted",
    accountDeletedDesc: "Bank account was successfully deleted.",
    lastSync: "Last sync",
    never: "Never",
    currentMonth: "Current month",
    last3Months: "Last 3 Months",
    last6Months: "Last 6 Months",
    wholeYear: "Whole year",
    syncComplete: "Sync complete",
    loadedTransactions: "Loaded",
    fromAccount: "transactions from account",
    forMonths: "for months",
    syncError: "Sync error",
    unknownError: "Unknown error",
    setupInstructions: "Setup instructions",
    getApiToken: "Getting API Token",
    downloadOlderData: "Downloading older data (90+ days)",
    multipleAccounts: "Multiple accounts",
    multipleAccountsDesc:
      "You can add multiple accounts (e.g. CZK and EUR account). Each account needs its own API token.",
    bankAccountsForInvoices: "Bank accounts for invoices (IBAN)",
    bankAccountsForInvoicesDesc:
      "Payment details (IBAN) shown on invoices. For transaction sync use API Accounts in Settings.",
    bankAccount: "Bank account",
    noBankAccount: "— None —",
    accountNumber: "Account number",
    bankCode: "Bank code",
    bankName: "Bank name",
    iban: "IBAN",
    swift: "SWIFT/BIC",
    addPaymentAccount: "Add account",
    editBankAccount: "Edit",
    // Shared status labels
    active: "Active",
    addFolderError: "Failed to add folder",
    skipped: "Skipped",
    // Analytics
    analytics: "Analytics",
    balance: "Balance",
    exchangeRate: "EUR/CZK Rate",
    // Pages
    dashboard: "Dashboard",
    invoices: "Invoices",
    clients: "Clients",
    templates: "Templates",
    settingsPage: "Settings",
    connecting: "Connecting…",
    profile: "Profile",
    general: "General",
    // Feedback
    tagImprovement: "Improvement",
    tagIdea: "Idea",
    tagBug: "Bug/Stopped Working",
    tagProfile: "Profile",
    tagOther: "Other",
    feedback: "Feedback",
    feedbackDesc: "Share your ideas, report issues, and help improve the application",
    addYourFirstNote: "Add your first note",
    noFeedbackYet: "No feedback yet",
    noFeedbackDesc: 'Be the first to share your thoughts! Click "Add Note" to get started.',
    editFeedbackNote: "Edit feedback note",
    addFeedbackNote: "Add feedback note",
    page: "Page",
    title: "Title",
    content: "Content",
    tags: "Tags",
    postAnonymously: "Post anonymously",
    yourName: "Your name",
    usingNameFromProfile: "Using name from your profile. You can change it here.",
    showMore: "Show more",
    showLess: "Show less",
    anonymous: "Anonymous",
    success: "Success",
    feedbackNoteCreated: "Feedback note created successfully",
    feedbackNoteUpdated: "Feedback note updated successfully",
    feedbackNoteDeleted: "Feedback note deleted successfully",
    failedToCreateNote: "Failed to create feedback note",
    failedToUpdateNote: "Failed to update feedback note",
    failedToDeleteNote: "Failed to delete feedback note",
    saving: "Saving...",
    create: "Create",
    update: "Update",
    deleteConfirm: "Are you sure you want to delete this feedback note?",
    // Import/Export
    transactions: "Transactions",
    receiptsOverview: "Receipts overview",
    importExport: "Import / Export",
    importTransactions: "Import transactions",
    exportTransactions: "Export transactions",
    selectCsvFile: "Select CSV file",
    preview: "Preview",
    import: "Import",
    export: "Export",
    transactionsImportedSuccessfully: "Transactions imported successfully",
    duplicatesSkipped: "duplicates skipped",
    syncErrorBannerTitle: "Bank account connection failed",
    syncErrorBannerDescription:
      "Could not fetch transactions from your account. Please re-save the API token in settings:",
    syncErrorBannerAction: "Go to settings",
  },
};

interface LanguageContextType {
  language: Language;
  // This is useState's setter, passed straight through, so it accepts the updater form.
  setLanguage: React.Dispatch<React.SetStateAction<Language>>;
  /**
   * The visitor picked a language (a switcher or Settings). Also stores the
   * choice in the zettl_lang cookie for the redirect on "/". URL- and
   * preference-driven changes use setLanguage and never write the cookie.
   */
  chooseLanguage: (language: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { data: preferences } = useUserPreferences();

  // Initialize from the URL, then localStorage (for immediate UI), then sync with database
  const [language, setLanguage] = useState<Language>(detectInitialLanguage);

  // Sync language from database when preferences load (do not override an explicit URL locale)
  useEffect(() => {
    if (!preferences?.default_language) {
      return;
    }
    // German was dropped: a stored "de" (or any other value) means English.
    const dbLanguage: Language = preferences.default_language === "cs" ? "cs" : "en";
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const urlMatch = path.match(/^\/(cs|en)(\/|$)/);
      if (urlMatch) {
        const urlLang = urlMatch[1] as Language;
        if (urlLang !== dbLanguage) {
          return;
        }
        // LanguageSync navigation may lag state by one tick — don't snap back to DB preference
        if (urlLang !== language) {
          return;
        }
      }
    }
    if (dbLanguage !== language) {
      setLanguage(dbLanguage);
      localStorage.setItem("language", dbLanguage);
    }
  }, [preferences?.default_language, language]);

  // Always persist to localStorage when language changes
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const chooseLanguage = useCallback((next: Language) => {
    persistLanguageCookie(next);
    setLanguage(next);
  }, []);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, chooseLanguage, t: translations[language] }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
